import { Armchair, ArrowRight, ShieldCheck, TrendingUp } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const PROGRAMS = [
    { name: "Otago Exercise Program", fit: "For frail or high-risk older adults who need individualized strength and balance work" },
    { name: "A Matter of Balance", fit: "For adults whose fear of falling keeps them from being active" },
    { name: "SAIL (Stay Active and Independent for Life)", fit: "For ongoing group strength and balance exercise" },
    {
        name: "Fall Safe Ready",
        fit: "For active adults ready to practice catching themselves, landing more safely and getting up from the floor",
        highlight: true,
    },
];

const POINTS = [
    {
        icon: ShieldCheck,
        title: "Safety first",
        text: "Every participant completes a health questionnaire, and anyone better served by Otago or A Matter of Balance is referred there.",
    },
    {
        icon: Armchair,
        title: "Easy to host",
        text: "A one-hour introductory workshop needs only a room, sturdy chairs and some open floor space.",
    },
    {
        icon: TrendingUp,
        title: "Measurable",
        text: "Participants complete simple before-and-after checks, so you can see results.",
    },
];

export default function Programs() {
    return (
        <section id="organizations" className="scroll-mt-24 pb-10 sm:pb-14 lg:pb-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <SectionHead eyebrow="For organizations & partners" title="A hands-on next step for the people you serve." />
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-soft">
                        Fall Safe Ready is built to work alongside established fall-prevention programs.
                        Here&rsquo;s where it fits:
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <ul className="mt-10 overflow-hidden rounded-3xl border border-clay bg-white">
                        {PROGRAMS.map((p) => (
                            <li
                                key={p.name}
                                data-testid={p.highlight ? "programs-row-fall-safe-ready" : undefined}
                                className={`grid gap-2 p-6 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-baseline md:gap-8 md:p-7 ${
                                    p.highlight
                                        ? "border-l-4 border-teal-600 bg-teal-50"
                                        : "border-l-4 border-transparent"
                                } border-b border-clay last:border-b-0`}
                            >
                                <h3 className="font-display text-xl font-extrabold text-ink">{p.name}</h3>
                                <p className="text-lg leading-relaxed text-ink-soft">{p.fit}</p>
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-4 text-base italic text-ink-soft">
                        Fall Safe Ready is an independent program and is not affiliated with or endorsed by these
                        programs.
                    </p>
                </Reveal>
                <div className="mt-14 grid gap-8 sm:grid-cols-3">
                    {POINTS.map((p, i) => (
                        <Reveal key={p.title} delay={i * 0.1} className="h-full">
                            <div className="h-full">
                                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-ink text-brand-amber" aria-hidden="true">
                                    <p.icon className="h-6 w-6" />
                                </span>
                                <h3 className="mt-4 font-display text-xl font-extrabold text-ink">{p.title}</h3>
                                <p className="mt-2 text-lg leading-relaxed text-ink-soft">{p.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            data-testid="programs-host-workshop-button"
                            onClick={() => scrollToId("signup")}
                            className="btn-primary text-lg"
                        >
                            Host a workshop
                            <ArrowRight className="h-5 w-5" aria-hidden="true" />
                        </button>
                        <a href="/flyer" data-testid="programs-flyer-link" className="btn-outline">
                            Print a one-page flyer
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}